import { Skeleton } from "@/shared/ui/skeleton";

/**
 * Route-level loading state while a lazy page or its first query resolves.
 *
 * **A skeleton mirrors the geometry it will become** (redesign 2026-09, pass 02 board `6a`). A stack
 * of equal grey bars is a different shape from the page that replaces it, so the layout jumps at the
 * moment the content arrives — which reads as the page breaking rather than as it loading. The two
 * shapes here are the two the product actually has under a page header: a table in a frame, and a
 * column of cards or fields.
 */
export function PageSkeleton({
  rows = 6,
  shape = "list",
}: {
  rows?: number;
  /**
   * `table` draws the real frame — a `--bar-h-region` column strip on `--surface-sunken`, then rows
   * at the compact height — so a list page settles into place instead of reflowing. `list` is the
   * card-and-field shape a settings tab or a record page becomes, and stays the default because a
   * mid-page caller (`<PageSkeleton rows={3} />` inside a tab) is almost always that one.
   */
  shape?: "list" | "table";
}) {
  return (
    <div aria-busy aria-label="Loading" className="flex flex-col gap-5">
      {/* The page header band: title at --text-display, the meta line under it. */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-5 w-72" />
      </div>
      {shape === "table" ? (
        <div className="overflow-hidden rounded-md border border-border bg-surface">
          <div className="flex h-(--bar-h-region) items-center gap-3 border-b border-border bg-surface-sunken px-3">
            <Skeleton className="h-2.5 w-10" />
            <Skeleton className="h-2.5 w-14" />
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="ml-auto h-2.5 w-16" />
          </div>
          {Array.from({ length: rows }, (_, i) => (
            <div
              key={i}
              className="flex h-(--row-h-compact) items-center gap-3 border-b border-border px-3 last:border-b-0"
            >
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-2.5 w-32" />
              <Skeleton className="ml-auto h-2.5 w-20" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {Array.from({ length: rows }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      )}
    </div>
  );
}
